import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const specializedSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Specialized Cleaning Services',
  description: 'Specialized commercial cleaning services in Louisville, KY including post-construction cleanup, carpet extraction, floor stripping and waxing, pressure washing, and emergency cleaning.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Specialized Cleaning',
};

export default function SpecializedServicesPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 10); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Specialized Cleaning Services Louisville KY | First Insight Cleaning"
        description="Expert specialized cleaning in Louisville, KY. Post-construction cleanup, carpet extraction, floor stripping &amp; waxing, pressure washing &amp; emergency cleaning. Customized solutions. Get a free quote."
        keywords="specialized cleaning Louisville KY, post-construction cleaning Kentucky, carpet extraction, floor waxing, First Insight Cleaning"
        canonical={`${siteUrl}/services/specialized-services`}
        schemaJson={specializedSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/738c3fca0c385c07ab40ac18873bb63f.jpg"
            alt="Specialized Cleaning Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Specialized Cleaning Services</h1>
          <p className="text-base md:text-xl max-w-2xl mx-auto">Expert solutions for unique cleaning challenges and special projects</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 mb-6">Comprehensive Specialized Cleaning Solutions</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              First Insight Cleaning offers a complete range of specialized cleaning services designed to address unique challenges that go beyond standard maintenance. Our expert team utilizes advanced techniques, specialized equipment, and industry-specific knowledge to deliver exceptional results for projects requiring extra attention and expertise.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Whether you need post-construction cleanup, carpet and upholstery restoration, floor refinishing, or emergency cleaning services, we have the skills and resources to handle any specialized cleaning requirement. Our customized approach ensures that each project receives the specific care and attention it demands.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hammer-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Post-Construction Cleanup</h3>
              <p className="text-slate-600">Thorough removal of construction debris, dust, and residue to prepare your space for occupancy.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-sofa-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Carpet and Upholstery Care</h3>
              <p className="text-slate-600">Deep cleaning and restoration services for carpets, rugs, and upholstered furniture using professional equipment.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-contrast-drop-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Floor Stripping and Waxing</h3>
              <p className="text-slate-600">Complete floor restoration including stripping, sealing, and waxing for a brilliant, long-lasting finish.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-flashlight-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Pressure Washing</h3>
              <p className="text-slate-600">High-pressure cleaning for exterior surfaces, parking lots, sidewalks, and building facades.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alarm-warning-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Emergency Cleaning</h3>
              <p className="text-slate-600">Rapid response cleaning services for unexpected situations requiring immediate attention.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-recycle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Green Cleaning Programs</h3>
              <p className="text-slate-600">Environmentally responsible cleaning using sustainable products and practices.</p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Our Specialized Services</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Expert technicians with specialized training and certifications</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Professional-grade equipment for superior results</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Customized solutions for unique cleaning challenges</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Fast turnaround times without compromising quality</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Comprehensive project management from start to finish</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Satisfaction guaranteed on all specialized services</span>
                </li>
              </ul>
            </div>
            <div className="h-[400px]">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_d304ee78b01d2bd8ff046a1fdb9b509c_a4abee80.jpg"
                alt="Professional carpet extraction cleaning in office"
                className="w-full h-full object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-teal-500 rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Need Specialized Cleaning Services?</h2>
            <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
              Contact us to discuss your unique cleaning requirements and get a customized solution
            </p>
            <Link 
              to="/#quote"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
